'use client';

import { useState } from 'react';
import styled from 'styled-components';
import { Store, ArrowRight } from 'lucide-react';
import { PageHeader } from '@/layout/page-header';
import { SummaryCards } from '@/app/(pages)/(home)/components/summary-cards';
import { PeriodSelector } from '@/app/(pages)/(home)/components/period-selector';
import { ExpenseCards } from '@/app/(pages)/(home)/components/expense-cards';
import { TopProducts } from '@/app/(pages)/(home)/components/top-products';
import { MarketplaceCard } from '@/app/(pages)/(home)/components/marketplace-card';
import { getDefaultPeriod } from '@/utils/date';
import { useDashboard } from './use-api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AddStoreModal } from '@/layout/connections/add-store-modal';

const WelcomeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
`;

const WelcomeCard = styled(Card)`
  max-width: 480px;
  width: 100%;
`;

const WelcomeContent = styled.div`
  padding: 40px 32px;
  text-align: center;
`;

const WelcomeIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  svg {
    color: var(--color-accent-ring);
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: var(--color-text-primary);
`;

const WelcomeText = styled.p`
  color: var(--color-text-secondary);
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ColumnsGrid = styled.div<{ $singleColumn?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: ${({ $singleColumn }) => $singleColumn ? '1fr' : 'repeat(2, 1fr)'};
  }
`;

const StoreColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StoreEntry = styled.div`
  display: flex;
  flex-direction: column;

  border-radius: 12px;
  border: 1px solid var(--color-border-default);
  background-color: var(--color-bg-surface);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

export function DashboardPageClient() {
  const defaultPeriod = getDefaultPeriod();
  const [period, setPeriod] = useState(defaultPeriod);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const { data, isLoading } = useDashboard(period.from, period.to);

  const hasNoConnections =
    !isLoading &&
    data &&
    data.wb_stores.length === 0 &&
    data.ozon_stores.length === 0;

  if (hasNoConnections) {
    return (
      <>
        <WelcomeContainer>
          <WelcomeCard>
            <WelcomeContent>
              <WelcomeIcon>
                <Store size={48} />
              </WelcomeIcon>
              <WelcomeTitle>Добро пожаловать!</WelcomeTitle>
              <WelcomeText>
                Подключите ваш первый магазин на Wildberries или Ozon,
                чтобы начать отслеживать продажи, расходы и прибыль
              </WelcomeText>
              <Button onClick={() => setIsAddModalOpen(true)}>
                Подключить магазин
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </WelcomeContent>
          </WelcomeCard>
        </WelcomeContainer>
        <AddStoreModal open={isAddModalOpen} onOpenChange={setIsAddModalOpen} />
      </>
    );
  }

  const hasWb = (data?.wb_stores.length ?? 0) > 0;
  const hasOzon = (data?.ozon_stores.length ?? 0) > 0;
  const singleMarketplace = !hasWb || !hasOzon;

  return (
    <div>
      <PageHeader title="Пульт" description="Центр управления бизнесом">
        <PeriodSelector
          from={period.from}
          to={period.to}
          onChange={(from, to) => setPeriod({ from, to })}
        />
      </PageHeader>

      <ContentStack>
        <SummaryCards
          summary={data?.summary}
          isLoading={isLoading}
        />

        <ColumnsGrid $singleColumn={singleMarketplace}>
          {hasWb && (
            <StoreColumn>
              {data!.wb_stores.map((store) => (
                <StoreEntry key={store.connection_uuid}>
                  <MarketplaceCard
                    marketplace="wildberries"
                    connection={{
                      uuid: store.connection_uuid,
                      marketplace: 'wildberries',
                      marketplace_store_id: '',
                      store_name: store.store_name,
                      key_status: store.key_status,
                      is_active: store.is_active,
                      last_sync_at: store.last_sync_at,
                      created_at: '',
                    }}
                    summary={store.dashboard.summary}
                    isLoading={isLoading}
                  />
                  <ExpenseCards
                    marketplace="wildberries"
                    wbSummary={store.dashboard.summary}
                    isLoading={isLoading}
                    storeName={store.store_name}
                  />
                </StoreEntry>
              ))}
            </StoreColumn>
          )}

          {hasOzon && (
            <StoreColumn>
              {data!.ozon_stores.map((store) => (
                <StoreEntry key={store.connection_uuid}>
                  <MarketplaceCard
                    marketplace="ozon"
                    connection={{
                      uuid: store.connection_uuid,
                      marketplace: 'ozon',
                      marketplace_store_id: '',
                      store_name: store.store_name,
                      key_status: store.key_status,
                      is_active: store.is_active,
                      last_sync_at: store.last_sync_at,
                      created_at: '',
                    }}
                    summary={store.dashboard.summary}
                    isLoading={isLoading}
                  />
                  <ExpenseCards
                    marketplace="ozon"
                    ozonSummary={store.dashboard.summary}
                    isLoading={isLoading}
                    storeName={store.store_name}
                  />
                </StoreEntry>
              ))}
            </StoreColumn>
          )}
        </ColumnsGrid>

        <TopProducts
          products={(data?.top_profitable ?? []).concat(data?.top_unprofitable ?? []).map(p => ({
            id: p.id,
            name: p.name,
            image_url: p.image_url || null,
            marketplace: p.marketplace,
            sales_count: p.sales_count,
            sku: p.sku || null,
            profit: parseFloat(p.profit),
            profit_display: p.profit,
            roi_display: p.roi,
            product_url: p.product_url,
          }))}
          isLoading={isLoading}
        />
      </ContentStack>
    </div>
  );
}
